import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useAuth } from '../../contexts/AuthContext'
import { validateEmail } from '../../utils/validation'
import {
  AuthContainer,
  AuthCard,
  Form,
  ButtonContainer,
  LinksContainer,
  StyledLink,
  Divider,
  SignupText,
  InputGroup,
  Label,
  StyledInput,
  ErrorText,
} from './styles'

export function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login, isAuthenticated } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/')
    }
  }, [isAuthenticated, navigate])

  const validateField = (name, value) => {
    let error = ''

    if (name === 'email') {
      if (!value) {
        error = 'Email é obrigatório'
      } else if (!validateEmail(value)) {
        error = 'Email inválido'
      }
    }

    if (name === 'password') {
      if (!value) {
        error = 'Senha é obrigatória'
      }
    }

    setErrors((prev) => ({ ...prev, [name]: error }))
    return !error
  }

  const onBlur = (name) => {
    setTouched((prev) => ({ ...prev, [name]: true }))
    if (name === 'email') validateField(name, email)
    if (name === 'password') validateField(name, password)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    setTouched({ email: true, password: true })
    const emailValid = validateField('email', email)
    const passwordValid = validateField('password', password)

    if (!emailValid || !passwordValid) {
      return
    }

    setIsLoading(true)

    try {
      await login(email, password)
      toast.success('Login realizado com sucesso!')

      const from = location.state?.from?.pathname || '/'
      navigate(from, { replace: true })
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Email ou senha inválidos')
      } else if (error.response?.status === 403) {
        toast.error('Email não verificado. Verifique sua caixa de entrada.')
        navigate('/resend-verification', { state: { email } })
      } else {
        toast.error('Erro ao fazer login. Tente novamente.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const isFormValid = !errors.email && !errors.password && email && password

  return (
    <AuthContainer>
      <AuthCard>
        <h1>Login</h1>
        <p>Entre com sua conta para acesso ilimitado</p>

        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="email">Email</Label>
            <StyledInput
              id="email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (touched.email) validateField('email', e.target.value)
              }}
              onBlur={() => onBlur('email')}
              placeholder="seu@email.com"
              autoComplete="email"
              $hasError={touched.email && errors.email}
              $isValid={touched.email && !errors.email && email}
            />
            {touched.email && errors.email && <ErrorText>✗ {errors.email}</ErrorText>}
          </InputGroup>

          <InputGroup>
            <Label htmlFor="password">Senha</Label>
            <StyledInput
              id="password"
              name="password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                if (touched.password) validateField('password', e.target.value)
              }}
              onBlur={() => onBlur('password')}
              placeholder="Sua senha"
              autoComplete="current-password"
              $hasError={touched.password && errors.password}
              $isValid={touched.password && !errors.password && password}
            />
            {touched.password && errors.password && <ErrorText>✗ {errors.password}</ErrorText>}
          </InputGroup>

          <ButtonContainer>
            <button type="submit" disabled={!isFormValid || isLoading}>
              {isLoading ? 'Entrando...' : 'Entrar'}
            </button>
          </ButtonContainer>
        </Form>

        <LinksContainer>
          <StyledLink to="/forgot-password">Esqueceu sua senha?</StyledLink>
        </LinksContainer>

        <Divider />

        <SignupText>
          Não tem uma conta? <StyledLink to="/register">Criar conta grátis</StyledLink>
        </SignupText>
      </AuthCard>
    </AuthContainer>
  )
}
